import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  CircularProgress,
  Paper,
  Divider,
  Container,
  Grid,
  Chip,
  IconButton,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import { getShippingCategoryById } from "../../../../../services/Admin/ShippingCategoriesApi";
import AddGovernoratesDialog from "./AddGovernoratesDialog";
import AddCitiesDialog from "./AddCitiesDialog";
import EditIcon from "@mui/icons-material/Edit";
import EditGovernorateDialog from "./EditGovernorateDialog";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteCityDialog from "./DeleteCityDialog";
import DeleteGovernorateDialog from "./DeleteGovernorateDialog";
import EditCityDialog from "./EditCityDialog";


const ShippingGovernotates = ({ countryId, categoryId }) => {
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState(null);
  const [error, setError] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [openCitiesDialog, setOpenCitiesDialog] = useState(false);

  // new: edit governorate dialog state
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedGovernorate, setSelectedGovernorate] = useState(null);

  // delete governorate state
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [governorateToDelete, setGovernorateToDelete] = useState(null);

  // edit city dialog state
  const [openEditCityDialog, setOpenEditCityDialog] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);

  // new: delete city state
  const [openDeleteCityDialog, setOpenDeleteCityDialog] = useState(false);
  const [cityToDelete, setCityToDelete] = useState(null);

  const fetch = async () => {
    setError("");
    setLoading(true);
    try {
      if (!categoryId) {
        setCategory(null);
        return;
      }
      const res = await getShippingCategoryById(categoryId);
      const data = res?.success ? res.data : res;
      setCategory(data || null);
    } catch (err) {
      setCategory(null);
      setError(err?.message || "فشل تحميل بيانات الشريحة");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  const formatMoney = (v) => {
    if (v == null || v === "") return "-";
    const n = Number(v);
    if (Number.isNaN(n)) return String(v);
    return n.toFixed(2);
  };

  // عرض التاريخ بصيغة 12 ساعة
  const formatDate12 = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(d);
  };

  const governorates = category?.governorates || [];
  const cities = category?.cities || [];
  const country = category?.country || null;

  // احسب المدن المرتبطة بالمحافظات و المدن المنفصلة
  const governorateIds = governorates.map((g) => Number(g.id));
  const standaloneCities = Array.isArray(cities)
    ? cities.filter((c) => !governorateIds.includes(Number(c.governorate_id)))
    : [];

  return (
    <Container maxWidth={false} sx={{ py: 2 }}>
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6">تفاصيل شريحة الشحن</Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              startIcon={<AddIcon />}
              onClick={() => setOpenDialog(true)}
              size="small"
              variant="contained"
              disabled={!categoryId}
            >
              إضافة محافظات
            </Button>

            <Button
              startIcon={<AddIcon />}
              onClick={() => setOpenCitiesDialog(true)}
              size="small"
              variant="contained"
              disabled={!categoryId || !governorates.length}
            >
              إضافة مدن
            </Button>

            <Button
              startIcon={<RefreshIcon />}
              onClick={fetch}
              disabled={loading}
              size="small"
              variant="outlined"
            >
              {loading ? "جارٍ..." : "تحديث"}
            </Button>
          </Stack>
        </Stack>

        {loading ? (
          <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ py: 2 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : !categoryId ? (
          <Typography color="text.secondary">اختر شريحة لعرض البيانات</Typography>
        ) : category ? (
          <>
            <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                <Box>
                  <Typography variant="subtitle2">المعلومات الأساسية</Typography>
                  <Typography>الاسم: {category.name}</Typography>
                  <Typography>التكلفة: {formatMoney(category.cost)}</Typography>
                  <Typography>الحالة: {Number(category.status) === 1 ? "مفعّل" : "معطّل"}</Typography>
                  <Typography>  (تم الانشاء): {formatDate12(category.created_at)}</Typography>
                  <Typography>آخر تعديل: {formatDate12(category.updated_at)}</Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2">الدولة</Typography>
                  {country ? (
                    <>
                      <Typography>#{country.id} - {country.name_ar || country.name_en}</Typography>
                      <Typography>العملة: {country.currency_ar || country.currency_en || "-"}</Typography>
                      <Typography>الحالة: {Number(country.status) === 1 ? "مفعّلة" : "معطّلة"}</Typography>
                    </>
                  ) : (
                    <Typography color="text.secondary">لا توجد دولة مرفقة</Typography>
                  )}
                </Box>

                <Box>
                  <Typography variant="subtitle2">مقاييس</Typography>
                  <Chip label={`المحافظات: ${governorates.length}`} size="small" sx={{ mr: 1 }} />
                  <Chip label={`المدن: ${cities.length}`} size="small" />
                </Box>
              </Stack>
            </Paper>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>المحافظات (Governorates)</Typography>
                  <Divider sx={{ mb: 1 }} />
                  {governorates.length ? (
                    <Stack spacing={1}>
                      {governorates.map((gov) => (
                        <Paper key={gov.id} sx={{ p: 1 }} variant="outlined">
                          <Stack spacing={1}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Box>
                                <Typography variant="body2" fontWeight={500}>{gov.name_ar || "-"}</Typography>
                                <Typography variant="caption" color="text.secondary">{gov.name_en || ""} • #{gov.id}</Typography>
                              </Box>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Chip label={Number(gov.status) === 1 ? "مفعّل" : "معطّل"} size="small" color={Number(gov.status) === 1 ? "success" : "error"} />
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedGovernorate(gov);
                                    setOpenEditDialog(true);
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    setGovernorateToDelete(gov);
                                    setOpenDeleteDialog(true);
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                                <Typography variant="caption" color="text.secondary">
                                  مدن: {Array.isArray(gov.cities) ? gov.cities.length : 0}
                                </Typography>
                              </Stack>
                            </Stack>

                            {/* عرض مدن المحافظة إن وجدت */}
                            {Array.isArray(gov.cities) && gov.cities.length ? (
                              <Stack spacing={1} sx={{ mt: 1 }}>
                                {gov.cities.map((city) => (
                                  <Box key={city.id} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 1, bgcolor: "grey.50", borderRadius: 1 }}>
                                    <Box>
                                      <Typography variant="body2">{city.name_ar || "-"}</Typography>
                                      <Typography variant="caption" color="text.secondary">{city.name_en || ""} • #{city.id}</Typography>
                                    </Box>
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          setSelectedCity(city);
                                          setOpenEditCityDialog(true);
                                        }}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => {
                                          setCityToDelete(city);
                                          setOpenDeleteCityDialog(true);
                                        }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                      <Chip label={Number(city.status) === 1 ? "مفعّل" : "معطّل"} size="small" color={Number(city.status) === 1 ? "success" : "error"} />
                                    </Stack>
                                  </Box>
                                ))}
                              </Stack>
                            ) : null}
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  ) : (
                    <Typography color="text.secondary">لا توجد محافظات</Typography>
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>المدن المرتبطة بالشريحة (cities)</Typography>
                  <Divider sx={{ mb: 1 }} />
                  {standaloneCities.length ? (
                    <Stack spacing={1}>
                      {standaloneCities.map((c) => (
                        <Box key={c.id} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 1, bgcolor: "grey.50", borderRadius: 1 }}>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>{c.name_ar}</Typography>
                            <Typography variant="caption" color="text.secondary">{c.name_en} • #{c.id}</Typography>
                          </Box>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedCity(c);
                                setOpenEditCityDialog(true);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setCityToDelete(c);
                                setOpenDeleteCityDialog(true);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                            <Chip label={Number(c.status) === 1 ? "مفعّل" : "معطّل"} size="small" color={Number(c.status) === 1 ? "success" : "error"} />
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography color="text.secondary">لا توجد مدن منفصلة</Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>

          </>
        ) : (
          <Typography color="text.secondary">لا توجد بيانات</Typography>
        )}
      </Paper>

      <AddGovernoratesDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onSuccess={fetch}
        categoryId={categoryId}
      />

      <EditGovernorateDialog
        open={openEditDialog}
        onClose={() => {
          setOpenEditDialog(false);
          setSelectedGovernorate(null);
        }}
        onSuccess={() => {
          setOpenEditDialog(false);
          setSelectedGovernorate(null);
          fetch();
        }}
        governorate={selectedGovernorate}
        categoryId={categoryId}
      />

      <DeleteGovernorateDialog
        open={openDeleteDialog}
        onClose={() => {
          setOpenDeleteDialog(false);
          setGovernorateToDelete(null);
        }}
        onSuccess={() => {
          setOpenDeleteDialog(false);
          setGovernorateToDelete(null);
          fetch();
        }}
        governorate={governorateToDelete}
      />

      <AddCitiesDialog
        open={openCitiesDialog}
        onClose={() => setOpenCitiesDialog(false)}
        onSuccess={() => {
          setOpenCitiesDialog(false);
          fetch();
        }}
        governorates={governorates}
        categoryId={categoryId}
      />

      <EditCityDialog
        open={openEditCityDialog}
        onClose={() => {
          setOpenEditCityDialog(false);
          setSelectedCity(null);
        }}
        onSuccess={() => {
          setOpenEditCityDialog(false);
          setSelectedCity(null);
          fetch();
        }}
        city={selectedCity}
        governorates={governorates}
        categoryId={categoryId}
      />

      <DeleteCityDialog
        open={openDeleteCityDialog}
        onClose={() => {
          setOpenDeleteCityDialog(false);
          setCityToDelete(null);
        }}
        onSuccess={() => {
          setOpenDeleteCityDialog(false);
          setCityToDelete(null);
          fetch();
        }}
        city={cityToDelete}
      />
    </Container>
  );
};

export default ShippingGovernotates;
